import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { useLocalStorage } from '@vueuse/core';

import { companyService } from '@/services/company';
import { API_ROUTES, apiRequest } from '@/types/api-routes';

import { useAuthStore } from './auth';

export interface OrganizationPathItem {
  id: string;
  name: string;
}

export interface Company {
  id: string;
  name: string;
  status: string;
  is_owner: boolean;
  organization_id?: string | null;
  organization_path?: OrganizationPathItem[] | null;
}

export const useCompanyStore = defineStore('company', () => {
  const lastCompanyId = useLocalStorage<string | null>('last_selected_company_id', null);
  const selectedCompanyId = ref<string | null>(null);
  const selectedCompany = ref<Company | null>(null);
  const availableCompanies = ref<Company[]>([]);
  const loading = ref(false);

  const isOwner = computed(() => selectedCompany.value?.is_owner ?? false);
  const hasSelectedCompany = computed(() => !!selectedCompanyId.value);

  async function loadAvailableCompanies(): Promise<void> {
    loading.value = true;

    const result = await apiRequest<Company[]>(API_ROUTES.customer.companies);

    loading.value = false;

    if (!result.ok) {
      console.error('Failed to load available companies:', result.error);
      return;
    }

    availableCompanies.value = result.data;
  }

  async function selectCompany(id: string): Promise<void> {
    const authStore = useAuthStore();

    const company = availableCompanies.value.find(c => c.id === id);

    if (company) {
      selectedCompany.value = company;
      selectedCompanyId.value = id;
      lastCompanyId.value = id;
    } else {
      if (availableCompanies.value.length === 0) {
        await loadAvailableCompanies();
        const foundCompany = availableCompanies.value.find(c => c.id === id);
        if (foundCompany) {
          selectedCompany.value = foundCompany;
          selectedCompanyId.value = id;
          lastCompanyId.value = id;

          const permissionsResult = await companyService.getCompanyPermissions(id);
          if (permissionsResult.ok) {
            authStore.updateCustomerPermissions(permissionsResult.data.permissions, permissionsResult.data.roles);
          } else {
            console.error('Failed to load company permissions:', permissionsResult.error);
          }
          return;
        }
      }

      selectedCompanyId.value = id;
      selectedCompany.value = null;
      lastCompanyId.value = id;
    }

    const permissionsResult = await companyService.getCompanyPermissions(id);
    if (permissionsResult.ok) {
      authStore.updateCustomerPermissions(permissionsResult.data.permissions, permissionsResult.data.roles);
    } else {
      console.error('Failed to load company permissions:', permissionsResult.error);
    }
  }

  function clearSelectedCompany(): void {
    selectedCompanyId.value = null;
    selectedCompany.value = null;
  }

  async function initialize(loadCompanies = true): Promise<void> {
    if (loadCompanies && availableCompanies.value.length === 0) {
      await loadAvailableCompanies();
    }
  }

  async function autoSelectCompany(): Promise<string | null> {
    if (availableCompanies.value.length === 0) {
      await loadAvailableCompanies();
    }

    const activeCompanies = availableCompanies.value.filter(c => c.status !== 'disabled');

    if (activeCompanies.length === 0) {
      return null;
    }

    if (activeCompanies.length === 1) {
      return activeCompanies[0].id;
    }

    const ownedCompany = activeCompanies.find(c => c.is_owner);
    if (ownedCompany) {
      return ownedCompany.id;
    }

    if (lastCompanyId.value && activeCompanies.some(c => c.id === lastCompanyId.value)) {
      return lastCompanyId.value;
    }

    return activeCompanies[0].id;
  }

  function reset(): void {
    selectedCompanyId.value = null;
    selectedCompany.value = null;
    availableCompanies.value = [];
  }

  return {
    selectedCompanyId,
    selectedCompany,
    availableCompanies,
    loading,

    isOwner,
    hasSelectedCompany,

    loadAvailableCompanies,
    selectCompany,
    clearSelectedCompany,
    autoSelectCompany,
    initialize,
    reset,
  };
});
